import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Tag, Star, ShoppingCart, Box, Loader2, AlertCircle, Ruler } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFigurineDetail } from '@/hooks/useFigurineDetail';
import FigurineGallery from '@/components/FigurineGallery';
import FigurineReviews from '@/components/FigurineReviews';
import AuthModal from '@/components/AuthModal';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';

const CATEGORY_COLORS: Record<string, string> = {
  'sci-fi': 'oklch(0.68 0.28 195)',
  'fantasy': 'oklch(0.65 0.32 280)',
  'filmy': 'oklch(0.65 0.26 150)',
  'bajki': 'oklch(0.72 0.24 45)',
  'personalizowane': 'oklch(0.62 0.28 15)',
  'wlasne': 'oklch(0.68 0.28 195)',
};

export default function FigurineDetailPage({ onCartOpen }: { onCartOpen?: () => void }) {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { figurine, media, reviews, loading, notFound } = useFigurineDetail(slug);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const { addItem } = useCart();
  const { user } = useAuth();

  const MIN_H = 10, MAX_H = 35;
  const [height, setHeight] = useState(MAX_H);

  const accentColor = CATEGORY_COLORS[figurine?.category_slug ?? ''] ?? 'var(--primary)';
  const hasModel = media.some(m => m.type === 'model3d');

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-10 h-10 text-primary animate-spin" />
    </div>
  );

  if (notFound || !figurine) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 px-4">
      <AlertCircle className="w-14 h-14 text-destructive opacity-60" />
      <h1 className="text-2xl font-black text-foreground" style={{ fontFamily: 'Orbitron, sans-serif' }}>
        NIE ZNALEZIONO FIGURKI
      </h1>
      <Button onClick={() => navigate('/')} variant="outline" className="gap-2">
        <ArrowLeft className="w-4 h-4" /> Wróć do kolekcji
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Top nav bar */}
      <div className="sticky top-0 z-30 border-b flex items-center gap-3 px-4 py-3"
        style={{ background: 'oklch(0.10 0.022 280 / 0.92)', borderColor: 'oklch(0.22 0.04 280)', backdropFilter: 'blur(14px)' }}>
        <button onClick={() => navigate('/')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm font-semibold"
          style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          <ArrowLeft className="w-4 h-4" /> Kolekcja
        </button>
        <span className="text-muted-foreground/40">/</span>
        <span className="text-sm font-bold text-foreground truncate" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          {figurine.title}
        </span>
        {hasModel && (
          <span className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
            style={{ background: 'oklch(0.68 0.28 195 / 0.15)', color: 'var(--accent)', border: '1px solid oklch(0.68 0.28 195 / 0.4)', fontFamily: 'JetBrains Mono, monospace' }}>
            <Box className="w-3 h-3" /> MODEL 3D
          </span>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

          {/* LEFT – Gallery */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            {media.length > 0 ? (
              <FigurineGallery items={media} title={figurine.title} accentColor={accentColor} />
            ) : figurine.image_url ? (
              <div className="w-full rounded-xl overflow-hidden border" style={{ aspectRatio: '9/16', borderColor: 'oklch(0.25 0.045 280)' }}>
                <img src={figurine.image_url} alt={figurine.title} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-full rounded-xl border flex items-center justify-center"
                style={{ aspectRatio: '9/16', background: 'oklch(0.16 0.03 280)', borderColor: 'oklch(0.25 0.045 280)', color: accentColor }}>
                <Box className="w-20 h-20 opacity-20" />
              </div>
            )}
          </motion.div>

          {/* RIGHT – Info + Reviews */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-8">

            {/* Category + Tags */}
            <div className="flex flex-wrap gap-2">
              {figurine.category_slug && (
                <span className="px-3 py-1 rounded-full text-xs font-bold tracking-wide"
                  style={{ background: `${accentColor}20`, color: accentColor, border: `1px solid ${accentColor}45`, fontFamily: 'Rajdhani, sans-serif' }}>
                  {figurine.category_slug.toUpperCase()}
                </span>
              )}
              {figurine.is_featured && (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: 'oklch(0.72 0.24 45 / 0.2)', color: 'var(--chart-4)', border: '1px solid oklch(0.72 0.24 45 / 0.4)', fontFamily: 'JetBrains Mono, monospace' }}>
                  <Star className="w-3 h-3 fill-current" /> FEATURED
                </span>
              )}
            </div>

            {/* Title */}
            <div>
              <h1 className="text-3xl sm:text-4xl font-black text-foreground leading-tight"
                style={{ fontFamily: 'Orbitron, sans-serif' }}>
                {figurine.title}
              </h1>
              {figurine.description && (
                <p className="text-muted-foreground mt-4 leading-relaxed text-base"
                  style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  {figurine.description}
                </p>
              )}
            </div>

            {/* Rating summary */}
            <div className="flex items-center gap-3">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className="w-5 h-5"
                    style={{ fill: s <= Math.round(figurine.avg_rating) ? 'var(--chart-4)' : 'transparent', color: s <= Math.round(figurine.avg_rating) ? 'var(--chart-4)' : 'var(--muted-foreground)' }} />
                ))}
              </div>
              <span className="font-black text-xl" style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--chart-4)' }}>
                {figurine.rating_count > 0 ? figurine.avg_rating.toFixed(1) : '—'}
              </span>
              <span className="text-sm text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                ({figurine.rating_count} {figurine.rating_count === 1 ? 'ocena' : figurine.rating_count < 5 ? 'oceny' : 'ocen'})
              </span>
            </div>

            {/* Tags */}
            {figurine.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {figurine.tags.map(tag => (
                  <span key={tag} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
                    style={{ background: 'oklch(0.20 0.035 280)', color: 'var(--muted-foreground)', fontFamily: 'JetBrains Mono, monospace' }}>
                    <Tag className="w-2.5 h-2.5" />#{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Height slider + Price + CTA */}
            {figurine.price_pln != null ? (() => {
              const basePrice = Number(figurine.price_pln);
              const displayPrice = basePrice * (height / MAX_H);
              const pct = ((height - MIN_H) / (MAX_H - MIN_H)) * 100;
              return (
                <div className="space-y-4 pt-2">

                  {/* Slider card */}
                  <div className="rounded-xl border p-4 space-y-3"
                    style={{ background: 'oklch(0.14 0.03 280)', borderColor: `${accentColor}40` }}>

                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Ruler className="w-4 h-4" style={{ color: accentColor }} />
                        <span className="text-xs font-bold tracking-widest text-muted-foreground"
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}>WYSOKOŚĆ WYDRUKU</span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-black tabular-nums"
                          style={{ fontFamily: 'Orbitron, sans-serif', color: accentColor }}>
                          {height}
                        </span>
                        <span className="text-base font-bold text-muted-foreground"
                          style={{ fontFamily: 'Rajdhani, sans-serif' }}>cm</span>
                      </div>
                    </div>

                    {/* Range input */}
                    <div className="relative py-1">
                      <input
                        type="range" min={MIN_H} max={MAX_H} step={1} value={height}
                        onChange={e => setHeight(Number(e.target.value))}
                        className="w-full h-2 rounded-full appearance-none cursor-pointer"
                        style={{
                          accentColor: accentColor,
                          background: `linear-gradient(to right, ${accentColor} ${pct}%, oklch(0.22 0.04 280) ${pct}%)`,
                        }}
                      />
                    </div>

                    {/* Min / Max labels */}
                    <div className="flex justify-between text-xs text-muted-foreground"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      <span>{MIN_H} cm (min)</span>
                      <span>{MAX_H} cm (maks.)</span>
                    </div>

                    {/* Price hint */}
                    {height < MAX_H && (
                      <p className="text-xs text-muted-foreground/60 text-center"
                        style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                        Cena bazowa {basePrice.toFixed(2)} zł dotyczy maksymalnej wysokości ({MAX_H} cm)
                      </p>
                    )}
                  </div>

                  {/* Price + CTA row */}
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground mb-0.5"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        Cena za {height} cm:
                      </span>
                      <span className="text-4xl font-black"
                        style={{ fontFamily: 'Orbitron, sans-serif', color: accentColor }}>
                        {displayPrice.toFixed(2)}{' '}
                        <span className="text-2xl">zł</span>
                      </span>
                    </div>
                    <Button size="lg" className="gap-2 flex-1 max-w-xs font-bold tracking-wider"
                      onClick={() => {
                        if (!user) { setAuthMode('login'); setAuthOpen(true); return; }
                        addItem(figurine.id);
                        onCartOpen?.();
                      }}
                      style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.75rem', background: `linear-gradient(135deg, var(--primary), var(--accent))`, boxShadow: `0 0 24px oklch(0.65 0.32 280 / 0.3)` }}>
                      <ShoppingCart className="w-4 h-4" /> DO KOSZYKA
                    </Button>
                  </div>
                </div>
              );
            })() : (
              <div className="pt-2">
                <Button size="lg" className="gap-2 font-bold tracking-wider"
                  onClick={() => {
                    if (!user) { setAuthMode('login'); setAuthOpen(true); return; }
                    addItem(figurine.id);
                    onCartOpen?.();
                  }}
                  style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.75rem', background: `linear-gradient(135deg, var(--primary), var(--accent))`, boxShadow: `0 0 24px oklch(0.65 0.32 280 / 0.3)` }}>
                  <ShoppingCart className="w-4 h-4" /> DO KOSZYKA
                </Button>
              </div>
            )}

            {/* Divider */}
            <div className="border-t" style={{ borderColor: 'oklch(0.22 0.04 280)' }} />

            {/* Reviews section */}
            <FigurineReviews
              reviews={reviews}
              figurineId={figurine.id}
              avgRating={figurine.avg_rating}
              ratingCount={figurine.rating_count}
              onAuthRequired={() => { setAuthMode('login'); setAuthOpen(true); }}
            />
          </motion.div>
        </div>
      </div>

      <AuthModal open={authOpen} mode={authMode} onClose={() => setAuthOpen(false)} onModeChange={m => setAuthMode(m)} />
    </div>
  );
}
